import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Checkbox from '@/components/Checkbox';
import FancySwitch from '@/components/FancySwitch';
import { RadioCircle, RadioInput } from '@/components/Forms';
import ShowHideButton from '@/components/ShowHideButton';
import Text from '@/components/Text';

import AttachedCountBadge from '../AttachedCountBadge';
import MoreImagesModal from '../MoreImagesModal';
import SimpleUploadedImageRow from '../SimpleUploadedImageRow';
import UploadMultipleImages from '../UploadMultipleImages';
import UploadSingleImage from '../UploadSingleImage';
import {
  AddMoreButton,
  Box,
  ButtonHolder,
  Header,
  HeaderButtons,
  Heading,
  ImageData,
  ImagesBox,
  LabelText,
  MoreImages,
  ResetButton,
  UploadedImages,
} from '../VariationOne';
import { GridInner, InputsGrid, NameTextArea } from '../VariationThree';
import {
  ImageGrid,
  StyledTextArea,
  StyledUploadMultipleImages,
  StyledUploadedImages,
  TextAreaBox,
  TextPlaceholder,
  UploadedMultipleImages,
} from '../VariationTwo';
import { getWatermarkPosition, getWatermarkTag } from '../shared';
import Filler from '../shared/Filler';
import {
  ActionBox,
  ActionTitle,
  StyledRadiosLabel,
} from '../shared/styledComponents';
import GeneratedImages from './GeneratedImages';
import UploadedImageRow from './UploadedImageRow';
import VariationsStyles from './VariationStyles';

export const CHANNEL_NAME_COLORS = ['black', 'blue'];

export const CHANNEL_IMAGE_OPTIONS = ['custom', 'reddit', 'anonymous'];

export interface ImageVariation {
  leftImageSrc: string;
  rightText: string;
  leftWatermarkTag: string;
  leftWatermarkPosition: string;
  textPosition: string;
  authorImage?: string;
  authorImageOriginal?: ImageData[];
  authorName: string;
  channelNameColor: string;
  brakes: boolean;
  channelImageOption: string;
}

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const UploadAuthorImages = styled(UploadSingleImage)`
  height: 98px;
`;

const ChannelImageHolder = styled.div`
  margin-top: 16px;
`;

const ChannelNameHolder = styled.div`
  margin-top: 16px;
`;

const HeadingsHolder = styled.div`
  flex: 1;
`;

interface Props {
  inArticle?: boolean;
}

const VariationSeven: FC<Props> = ({ inArticle }) => {
  const [multipleVariations, setMultipleVariations] = useState(false);
  const [showContent, setShowContent] = useState(true);
  const [brakes, setBrakes] = useState(false);
  const [text, setText] = useState('');
  const [leftImages, setLeftImages] = useState<ImageData[]>([]);
  const [showMoreImagesModal, setShowMoreImagesModal] = useState(false);
  const [variations, setVariations] = useState<ImageVariation[]>([]);
  const [authorImages, setAuthorImages] = useState<ImageData[]>([]);
  const [channelImageOption, setChannelImageOption] = useState('custom');

  const [authorName, setAuthorName] = useState('');
  const [nameColor, setNameColor] = useState('black');
  const [images, setImages] = useState<string[]>();

  const onReset = () => {
    setLeftImages([]);
    setVariations([]);
    setImages([]);
    setAuthorImages([]);
    setBrakes(false);
    setText('');
    setAuthorName('');
    setNameColor('black');
    setChannelImageOption('custom');
  };

  const generateVariations = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const newVariations = leftImages.map((leftImage) => ({
      leftImageSrc:
        leftImage.croppedImage ?? URL.createObjectURL(leftImage.image),
      rightText: multipleVariations ? leftImage.text : text,
      brakes: multipleVariations ? leftImage.brakes : brakes,
      textPosition: leftImage.textPosition,
      leftWatermarkTag: getWatermarkTag(leftImage),
      leftWatermarkPosition: getWatermarkPosition(leftImage),
      authorName: multipleVariations ? leftImage.authorName ?? '' : authorName,
      authorImage: multipleVariations
        ? leftImage.authorImage
        : !!authorImages.length
          ? authorImages[0]?.croppedImage ??
            URL.createObjectURL(authorImages[0].image)
          : undefined,
      channelNameColor: multipleVariations ? leftImage.nameColor! : nameColor,
      channelImageOption: multipleVariations
        ? leftImage.channelImageOption!
        : channelImageOption,
    }));

    setVariations(newVariations);
  };

  const renderLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
    />
  ));

  const renderMultipleLeftImages = leftImages.map((image, index) => (
    <UploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setLeftImages}
      hasText
      multipleVariations
    />
  ));

  const renderAuthorImages = authorImages.map((image, index) => (
    <SimpleUploadedImageRow
      key={`${index}-${image.image.name}-${image.image.lastModified}`}
      imageIndex={index}
      image={image}
      setImages={setAuthorImages}
    />
  ));

  const isResetDisabled =
    !leftImages?.length &&
    !text &&
    !brakes &&
    !authorImages.length &&
    !authorName &&
    nameColor === 'black' &&
    channelImageOption === 'custom';

  return (
    <>
      <Box>
        <Header>
          <HeadingsHolder>
            <Heading $type="button">Full Image + Comment</Heading>
            <Text $type="smallHeadline" $color="gray">
              Attach one full image and add text with the necessary parameters.
              Additionally, include the author's image and name
            </Text>
          </HeadingsHolder>
          <HeaderButtons>
            <FancySwitch
              isActive={multipleVariations}
              onClick={() => setMultipleVariations((prev) => !prev)}
              text="Multiple Variations:"
            />
            <ResetButton
              color="red"
              disabled={isResetDisabled}
              onClick={onReset}
            >
              Reset
            </ResetButton>
            <ShowHideButton
              showContent={showContent}
              onClick={() => setShowContent((prev) => !prev)}
            />
          </HeaderButtons>
        </Header>
        {showContent && (
          <>
            {multipleVariations ? (
              <ImagesBox $hasImages={!!leftImages?.length}>
                <LabelText $type="smallHeadline">Full Images</LabelText>
                {!!leftImages?.length ? (
                  <>
                    <UploadedMultipleImages>
                      {renderMultipleLeftImages}
                    </UploadedMultipleImages>
                    <MoreImages>
                      <AttachedCountBadge count={leftImages.length} />
                      <AddMoreButton
                        onClick={() => setShowMoreImagesModal(true)}
                        color="colorized"
                      >
                        Add more images
                      </AddMoreButton>
                    </MoreImages>
                  </>
                ) : (
                  <UploadMultipleImages
                    coverIndex={1}
                    noneDivider
                    setImages={setLeftImages}
                  />
                )}
              </ImagesBox>
            ) : (
              <>
                <ImageGrid>
                  <ImagesBox $hasImages={!!leftImages?.length}>
                    <LabelText $type="smallHeadline">Full Images</LabelText>
                    {!!leftImages?.length ? (
                      <>
                        <StyledUploadedImages>
                          {renderLeftImages}
                        </StyledUploadedImages>
                        <MoreImages>
                          <AttachedCountBadge count={leftImages.length} />
                          <AddMoreButton
                            onClick={() => setShowMoreImagesModal(true)}
                            color="colorized"
                          >
                            Add more images
                          </AddMoreButton>
                        </MoreImages>
                      </>
                    ) : (
                      <StyledUploadMultipleImages
                        coverIndex={1}
                        setImages={setLeftImages}
                        noneDivider
                      />
                    )}
                  </ImagesBox>
                  <RightColumn>
                    <TextAreaBox>
                      <div>
                        <LabelText $type="smallHeadline">Right Text</LabelText>
                        <StyledTextArea
                          onChange={(e) => setText(e.target.value)}
                          value={text}
                          placeholder="Add text"
                        ></StyledTextArea>
                      </div>
                      <div>
                        <ActionTitle $type="smallHeadline">
                          Text Parameters
                        </ActionTitle>
                        <ActionBox>
                          <Checkbox
                            checked={brakes}
                            onChange={() => setBrakes((prev) => !prev)}
                          >
                            Custom brakes
                          </Checkbox>
                        </ActionBox>
                      </div>
                    </TextAreaBox>
                    {!!leftImages?.length && <TextPlaceholder />}
                  </RightColumn>
                </ImageGrid>
                <InputsGrid>
                  <GridInner $noBottomPadding={!!authorImages?.length}>
                    <LabelText $type="smallHeadline">Channel Image</LabelText>
                    <ActionBox>
                      {CHANNEL_IMAGE_OPTIONS.map((option) => (
                        <StyledRadiosLabel key={option}>
                          {option}
                          <RadioInput
                            type="radio"
                            name="channel_image"
                            checked={channelImageOption === option}
                            onChange={() => setChannelImageOption(option)}
                          />
                          <RadioCircle />
                        </StyledRadiosLabel>
                      ))}
                    </ActionBox>
                    {channelImageOption === 'custom' && (
                      <ChannelImageHolder>
                        <LabelText $type="smallHeadline">
                          Add Channel Image
                        </LabelText>
                        {!!authorImages?.length ? (
                          <UploadedImages>{renderAuthorImages}</UploadedImages>
                        ) : (
                          <UploadAuthorImages
                            coverIndex={2}
                            setImages={setAuthorImages}
                          />
                        )}
                      </ChannelImageHolder>
                    )}
                  </GridInner>
                  <GridInner>
                    <LabelText $type="smallHeadline">
                      Channel Name Color
                    </LabelText>
                    <ActionBox>
                      {CHANNEL_NAME_COLORS.map((color) => (
                        <StyledRadiosLabel key={color}>
                          {color}
                          <RadioInput
                            type="radio"
                            name="name_color"
                            checked={nameColor === color}
                            onChange={() => setNameColor(color)}
                          />
                          <RadioCircle />
                        </StyledRadiosLabel>
                      ))}
                    </ActionBox>
                    <ChannelNameHolder>
                      <LabelText $type="smallHeadline">Channel Name</LabelText>
                      <NameTextArea
                        placeholder="Add Name"
                        onChange={(e) => setAuthorName(e.target.value)}
                        value={authorName}
                      ></NameTextArea>
                    </ChannelNameHolder>
                  </GridInner>
                </InputsGrid>
              </>
            )}
          </>
        )}
      </Box>
      <VariationsStyles />
      <Filler />
      {showContent && (
        <ButtonHolder>
          <Button disabled={!leftImages?.length} onClick={generateVariations}>
            {!!variations?.length ? 'Regenerate' : 'Generate'}
          </Button>
        </ButtonHolder>
      )}
      <GeneratedImages
        variations={variations}
        images={images}
        setImages={setImages}
        inArticle={inArticle}
        onReset={onReset}
      />
      <AnimatePresence>
        {!!showMoreImagesModal && (
          <MoreImagesModal
            onClose={() => setShowMoreImagesModal(false)}
            setImages={setLeftImages}
            coverIndex={1}
            noneDivider
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default VariationSeven;
